import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './CustomerTierForm.css';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import NumberInput from '../../../shared/NumberInput';
import { getTenantHomeCurrency } from '../../../shared/numberFormat';

function CustomerTierForm({ initial, onSave, onCancel, loading }) {
  const [name, setName] = useState(initial?.name || '');
  const [paymentTermId, setPaymentTermId] = useState(initial?.payment_term_id ?? '');
  const [creditLimit, setCreditLimit] = useState(initial?.credit_limit ?? '');
  const [taxHandlingTypeId, setTaxHandlingTypeId] = useState(initial?.tax_handling_type_id ?? '');
  // Optional — the Customer Price Group every Entity on this Tier is priced
  // at unless it has its own assignment (vis_relationship_lifecycle.md §6),
  // e.g. Consumer -> Retail, so Retail is always a deliberate choice.
  const [defaultCustomerPriceGroupId, setDefaultCustomerPriceGroupId] = useState(initial?.default_customer_price_group_id ?? '');
  const [customerPriceGroups, setCustomerPriceGroups] = useState([]);
  const [error, setError] = useState('');
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [taxHandlingTypes, setTaxHandlingTypes] = useState([]);

  useEffect(() => {
    axios.get('/api/payment-terms', { params: { status: 'active' } })
      .then((res) => setPaymentTerms(res.data.data || []))
      .catch(() => setPaymentTerms([]));
    axios.get('/api/tax-handling-types', { params: { status: 'active' } })
      .then((res) => setTaxHandlingTypes(res.data.data || []))
      .catch(() => setTaxHandlingTypes([]));
    axios.get('/api/customer-price-groups', { params: { status: 'active' } })
      .then((res) => setCustomerPriceGroups(res.data.data || []))
      .catch(() => setCustomerPriceGroups([]));
  }, []);

  // Active-only options, but keep the currently-assigned Payment Term visible
  // even if it has since been deactivated, so editing an existing Tier doesn't
  // silently hide its own reference.
  const paymentTermOptions = paymentTerms.filter(
    (pt) => pt.is_active || String(pt.id) === String(paymentTermId)
  );
  const selectedPaymentTerm = initial?.payment_term;
  if (selectedPaymentTerm && !paymentTermOptions.some((pt) => String(pt.id) === String(selectedPaymentTerm.id))) {
    paymentTermOptions.push(selectedPaymentTerm);
  }

  // Same active-plus-currently-assigned pattern as Payment Term above.
  const taxHandlingTypeOptions = taxHandlingTypes.filter(
    (t) => t.is_active || String(t.id) === String(taxHandlingTypeId)
  );
  const selectedTaxHandlingType = initial?.tax_handling_type;
  if (selectedTaxHandlingType && !taxHandlingTypeOptions.some((t) => String(t.id) === String(selectedTaxHandlingType.id))) {
    taxHandlingTypeOptions.push(selectedTaxHandlingType);
  }

  const validate = () => {
    if (!name.trim()) return 'Name is required.';
    if (!paymentTermId) return 'Payment Term is required.';
    if (!taxHandlingTypeId) return 'Tax Handling Type is required.';
    if (creditLimit !== '' && (isNaN(Number(creditLimit)) || Number(creditLimit) < 0)) {
      return 'Credit Limit must be a non-negative number.';
    }
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');
    onSave({
      name: name.trim(),
      payment_term_id: Number(paymentTermId),
      credit_limit: creditLimit === '' ? '' : Number(creditLimit),
      tax_handling_type_id: Number(taxHandlingTypeId),
      default_customer_price_group_id: defaultCustomerPriceGroupId === '' ? null : Number(defaultCustomerPriceGroupId),
    });
  };

  return (
    <form className="customer-tier-form" onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <label>Name<RequiredMark /></label><br />
          <input
            className="styled-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="e.g., Trade"
          />
        </div>
        <div>
          <label>Payment Term<RequiredMark /></label><br />
          <select
            className="styled-input"
            value={paymentTermId}
            onChange={(e) => setPaymentTermId(e.target.value)}
            style={{ minWidth: 160 }}
          >
            <option value="">Select...</option>
            {paymentTermOptions.map((pt) => (
              <option key={pt.id} value={pt.id}>{pt.name}{!pt.is_active ? ' (inactive)' : ''}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Credit Limit ({getTenantHomeCurrency()})</label><br />
          <NumberInput
            decimals={2}
            min={0}
            step="0.01"
            value={creditLimit}
            onChange={(e) => setCreditLimit(e.target.value)}
            placeholder="No credit"
            style={{ width: 130 }}
          />
        </div>
        <div>
          <label>Tax Handling Type<RequiredMark /></label><br />
          <select
            className="styled-input"
            value={taxHandlingTypeId}
            onChange={(e) => setTaxHandlingTypeId(e.target.value)}
            style={{ minWidth: 200 }}
          >
            <option value="">Select...</option>
            {taxHandlingTypeOptions.map((t) => (
              <option key={t.id} value={t.id}>{t.name}{!t.is_active ? ' (inactive)' : ''}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Default Customer Price Group</label><br />
          <select
            className="styled-input"
            value={defaultCustomerPriceGroupId}
            onChange={(e) => setDefaultCustomerPriceGroupId(e.target.value)}
            style={{ minWidth: 200 }}
          >
            <option value="">None</option>
            {customerPriceGroups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default CustomerTierForm;
